import { useCallback } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import AssetManagerService, { getAssetManagerErrorMessage } from '../services/AssetManager/assetManager.service';
import { assetManagerServiceMessages } from '../services/AssetManager/assetManager.constants';
import Notification from '../shared/components/Notification';
import { NotificationTypes } from '../enums/notificationTypes';
import { QueryKeys } from '../enums/queryKeys';
import type { AssetLayerField, AssetListConfigInput } from '../types/InfrastructureManagerTypes';

const NO_FIELDS: AssetLayerField[] = [];

export const useAssetListConfig = () => {
  const queryClient = useQueryClient();
  const { getListConfig, saveListConfig, setLayerKeyField } = AssetManagerService();
  const { data: config, isLoading: isLoadingConfig, isLoadingError: isConfigLoadingError } = useQuery(getListConfig());

  const { mutateAsync, isPending: isSavingConfig } = useMutation({
    ...saveListConfig(),
    onSuccess: (savedConfig) => {
      queryClient.setQueryData([QueryKeys.GET_ASSET_LIST_CONFIG], savedConfig);
      // Pages of the previous configuration can never be shown again.
      queryClient.removeQueries({ queryKey: [QueryKeys.GET_ASSET_LIST_PAGE] });
      Notification({ message: assetManagerServiceMessages.LIST_CONFIG.SAVE_SUCCESS, type: NotificationTypes.SUCCESS });
    },
    onError: (error) => {
      Notification({
        message: assetManagerServiceMessages.LIST_CONFIG.SAVE_ERROR,
        description: getAssetManagerErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    },
  });

  const saveConfig = useCallback((rows: AssetListConfigInput[]) => mutateAsync(rows), [mutateAsync]);

  // A layer can only hold assets once it has a key field; the API rejects one with empty or duplicate values.
  const { mutateAsync: setKeyFieldAsync, isPending: isSettingKeyField } = useMutation({
    ...setLayerKeyField(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_ASSET_MANAGER_META_LAYERS] }),
    onError: (error) => {
      Notification({
        message: assetManagerServiceMessages.LIST_CONFIG.KEY_FIELD_ERROR,
        description: getAssetManagerErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    },
  });

  const setKeyField = useCallback((layerId: number, keyField: string) => setKeyFieldAsync({ layerId, keyField }), [setKeyFieldAsync]);

  return { config, isLoadingConfig, isConfigLoadingError, saveConfig, isSavingConfig, setKeyField, isSettingKeyField };
};

// Editor-only data: mounted with the config popover, so nothing is fetched or held while it is closed.
export const useAssetListEditorOptions = (selectedLayerIds: number[]) => {
  const { getMetaLayers, getLayerFields } = AssetManagerService();
  const { data: layers, isLoading: isLoadingLayers, error: layersError } = useQuery(getMetaLayers());

  const { fieldsByLayerId, fieldsError } = useQueries({
    queries: selectedLayerIds.map((layerId) => getLayerFields(layerId)),
    combine: (results) => ({
      fieldsByLayerId: new Map(
        selectedLayerIds.map((layerId, index) => [
          layerId,
          {
            fields: results[index].data?.fields ?? NO_FIELDS,
            suggestedKeyField: results[index].data?.suggestedKeyField ?? null,
            isLoading: results[index].isLoading,
          },
        ]),
      ),
      fieldsError: results.find(({ error }) => error)?.error ?? null,
    }),
  });

  return { layers, isLoadingLayers, layersError, fieldsByLayerId, fieldsError };
};
